import { computed, ref, unref } from 'vue';
import { useRoute } from 'vue-router';
import ImovelAPI from 'dashboard/api/imoveis';
import { useAlert } from 'dashboard/composables';
import { useImoveisStore } from 'dashboard/stores/imoveis';
import {
  PRICE_ESTIMATE_CONFIDENCE_LABELS,
  PRICE_ESTIMATE_HISTORY_LIMIT,
  PRICE_ESTIMATE_SOURCE_FALLBACK,
} from '../utils/imovelShowConstants';
import {
  actionErrorMessage,
  compactDifferencePercent,
  formatBRL,
  formatDateTime,
  positiveNumber,
} from '../utils/imovelShowFormatters';

/**
 * Estimativa QPreço: solicitação, revisão e aplicação com auditoria.
 *
 * @param {object} options
 * @param {import('vue').Ref<object>} options.imovel
 * @param {import('vue').Ref<object>} options.additionalAttributes
 * @param {import('vue').Ref<object>} options.rawAdditionalAttributes
 * @param {import('vue').Ref<number>} options.recommendedValue
 * @param {Function} options.refreshImovel
 */
export function useImovelPriceEstimate({
  imovel,
  additionalAttributes,
  rawAdditionalAttributes,
  recommendedValue,
  refreshImovel,
}) {
  const route = useRoute();
  const store = useImoveisStore();

  const priceEstimate = ref(null);
  const priceEstimateRequestedAt = ref('');
  const priceEstimateLoading = ref(false);
  const priceEstimateApplying = ref(false);
  const priceEstimateError = ref('');
  const priceApplyReason = ref('');

  const isRentListingContext = computed(() =>
    ['aluguel', 'temporada'].includes(unref(imovel)?.finalidade)
  );
  const priceTargetField = computed(() =>
    isRentListingContext.value ? 'valor_aluguel' : 'valor_venda'
  );
  const priceEstimateSource = computed(
    () =>
      priceEstimate.value?.source ||
      priceEstimate.value?.fonte ||
      PRICE_ESTIMATE_SOURCE_FALLBACK
  );
  const priceEstimateConfidence = computed(
    () =>
      priceEstimate.value?.confidence ||
      priceEstimate.value?.confidence_level ||
      priceEstimate.value?.confidenceLevel ||
      'insufficient_data'
  );
  const priceEstimateConfidenceLabel = computed(
    () =>
      PRICE_ESTIMATE_CONFIDENCE_LABELS[priceEstimateConfidence.value] ||
      priceEstimateConfidence.value ||
      'Não informada'
  );
  const priceEstimateSamples = computed(() => {
    const value =
      priceEstimate.value?.samples ??
      priceEstimate.value?.comparables_count ??
      priceEstimate.value?.comparablesCount ??
      priceEstimate.value?.comparables?.length ??
      0;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  });
  const estimatedPriceValue = computed(() =>
    positiveNumber(
      priceEstimate.value?.estimated_price ??
        priceEstimate.value?.estimatedPrice ??
        priceEstimate.value?.recommended_price ??
        priceEstimate.value?.recommendedPrice ??
        priceEstimate.value?.median_price ??
        priceEstimate.value?.medianPrice
    )
  );
  const priceEstimateRangeLow = computed(() =>
    positiveNumber(
      priceEstimate.value?.price_range_low ??
        priceEstimate.value?.priceRangeLow ??
        priceEstimate.value?.price_p25 ??
        priceEstimate.value?.priceP25
    )
  );
  const priceEstimateRangeHigh = computed(() =>
    positiveNumber(
      priceEstimate.value?.price_range_high ??
        priceEstimate.value?.priceRangeHigh ??
        priceEstimate.value?.price_p75 ??
        priceEstimate.value?.priceP75
    )
  );
  const priceEstimateRangeLabel = computed(() => {
    if (priceEstimateRangeLow.value && priceEstimateRangeHigh.value) {
      return `${formatBRL(priceEstimateRangeLow.value)} a ${formatBRL(
        priceEstimateRangeHigh.value
      )}`;
    }

    return 'Faixa indisponível';
  });
  const priceEstimateDateLabel = computed(() =>
    formatDateTime(
      priceEstimate.value?.generated_at ||
        priceEstimate.value?.generatedAt ||
        priceEstimateRequestedAt.value
    )
  );
  const priceEstimateEmptyMessage = computed(
    () =>
      priceEstimate.value?.message ||
      'Sem comparáveis suficientes para esta combinação de bairro, tipo e área.'
  );
  const hasApplicablePriceEstimate = computed(() =>
    Boolean(estimatedPriceValue.value)
  );
  const priceApplicationHistory = computed(() => {
    const records =
      unref(rawAdditionalAttributes).price_estimate_applications ||
      unref(additionalAttributes).priceEstimateApplications ||
      [];

    return Array.isArray(records)
      ? records.slice(-PRICE_ESTIMATE_HISTORY_LIMIT).reverse()
      : [];
  });

  const priceEstimatePayload = () => ({
    bairro: unref(imovel)?.bairro,
    cidade: unref(imovel)?.cidade,
    tipo: unref(imovel)?.tipo,
    finalidade: unref(imovel)?.finalidade,
    area_util: unref(imovel)?.areaUtil,
    quartos: unref(imovel)?.quartos,
    window_days: 180,
  });

  const priceEstimateErrorMessage = error =>
    actionErrorMessage(
      error,
      'Não foi possível estimar preço agora. As demais ações do imóvel seguem disponíveis.'
    );

  const solicitarEstimativaPreco = async () => {
    priceEstimateLoading.value = true;
    priceEstimateError.value = '';
    try {
      const { data } = await ImovelAPI.estimatePrice(priceEstimatePayload());
      priceEstimate.value = data || {};
      priceEstimateRequestedAt.value = new Date().toISOString();

      if (!hasApplicablePriceEstimate.value) {
        useAlert(priceEstimateEmptyMessage.value);
      }
    } catch (error) {
      priceEstimateError.value = priceEstimateErrorMessage(error);
      useAlert(priceEstimateError.value);
    } finally {
      priceEstimateLoading.value = false;
    }
  };

  const buildPriceEstimateApplication = () => {
    const appliedPrice = estimatedPriceValue.value;
    const previousPrice = positiveNumber(unref(recommendedValue));
    const contractStatus =
      unref(imovel)?.contractStatus ||
      unref(rawAdditionalAttributes).contract_status ||
      unref(rawAdditionalAttributes).contractStatus ||
      '';

    return {
      source: priceEstimateSource.value,
      reason: priceApplyReason.value.trim(),
      target_field: priceTargetField.value,
      finalidade: unref(imovel)?.finalidade,
      previous_price: previousPrice,
      applied_price: appliedPrice,
      estimated_price: appliedPrice,
      difference_value:
        previousPrice === null
          ? null
          : Number((appliedPrice - previousPrice).toFixed(2)),
      difference_percent: compactDifferencePercent(appliedPrice, previousPrice),
      confidence: priceEstimateConfidence.value,
      samples: priceEstimateSamples.value,
      price_range_low: priceEstimateRangeLow.value,
      price_range_high: priceEstimateRangeHigh.value,
      estimate_requested_at: priceEstimateRequestedAt.value,
      confirmed_at: new Date().toISOString(),
      existing_contract: Boolean(contractStatus),
      contract_status: contractStatus,
      estimate_payload: priceEstimate.value,
    };
  };

  const mergePriceEstimateApplications = application => {
    const existing = Array(
      unref(rawAdditionalAttributes).price_estimate_applications ||
        unref(additionalAttributes).priceEstimateApplications
    )
      .flat()
      .filter(Boolean);

    return [...existing, application].slice(-PRICE_ESTIMATE_HISTORY_LIMIT);
  };

  const canApplyPriceEstimate = () => {
    if (!hasApplicablePriceEstimate.value) {
      useAlert('Solicite uma estimativa com comparáveis antes de aplicar.');
      return false;
    }

    if (!priceApplyReason.value.trim()) {
      useAlert('Informe o motivo para aplicar a estimativa.');
      return false;
    }

    return true;
  };

  const priceEstimateConfirmDescription = computed(
    () =>
      `Aplicar ${formatBRL(estimatedPriceValue.value)} como ${
        isRentListingContext.value ? 'aluguel' : 'valor de venda'
      }?`
  );

  const aplicarEstimativaPreco = async () => {
    if (!canApplyPriceEstimate()) {
      return;
    }

    const nextPrice = estimatedPriceValue.value;

    const application = buildPriceEstimateApplication();
    priceEstimateApplying.value = true;
    try {
      await store.update({
        id: route.params.id,
        [priceTargetField.value]: nextPrice,
        additional_attributes: {
          ...unref(rawAdditionalAttributes),
          price_estimate_application: application,
          price_estimate_applications:
            mergePriceEstimateApplications(application),
        },
      });
      await refreshImovel();
      useAlert('Estimativa aplicada e histórico registrado.');
    } catch (error) {
      useAlert(
        actionErrorMessage(error, 'Não foi possível aplicar a estimativa.')
      );
    } finally {
      priceEstimateApplying.value = false;
    }
  };

  return {
    priceEstimate,
    priceEstimateLoading,
    priceEstimateApplying,
    priceEstimateError,
    priceApplyReason,
    priceEstimateSource,
    priceEstimateConfidenceLabel,
    priceEstimateSamples,
    estimatedPriceValue,
    priceEstimateRangeLabel,
    priceEstimateDateLabel,
    priceEstimateEmptyMessage,
    hasApplicablePriceEstimate,
    priceApplicationHistory,
    canApplyPriceEstimate,
    priceEstimateConfirmDescription,
    solicitarEstimativaPreco,
    aplicarEstimativaPreco,
  };
}

export default useImovelPriceEstimate;
